"use client";

import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useEntityContextOptional } from "../contexts/EntityContextProvider";
import {
  ChatPage as MeridianChatPage,
  ChatSelectionProvider,
  ChatDataProvider,
} from "@west-tennessee-consulting/meridian";
import { EntityContextProvider as MeridianEntityContextProvider } from "@west-tennessee-consulting/meridian/contexts/EntityContextProvider";
import { FloatingModal } from "./FloatingModal";
import { useGlobalModalStack, MODAL_IDS, ResponsiveModal } from "@west-tennessee-consulting/ui";
import { useMediaQueryWithMount } from "@/lib/hooks/ui/use-media-query";
import type { AskMeridianModalData } from "@/lib/modals/data/modal-data-types";

/**
 * Inner component that renders the focused chat.
 *
 * Entity context is injected into the chat requests natively by the Meridian
 * package's transport (it reads the MeridianEntityContextProvider this modal
 * mounts), so no global fetch interception is needed here.
 */
function EntityFocusedChatPageInner() {
  return (
    <div className="h-full w-full">
      <MeridianChatPage apiBaseUrl="/api/meridian/entity-chat" />
    </div>
  );
}

/**
 * AskMeridianModal component
 * Modal that opens Meridian chat with entity context
 * Uses MeridianChatPage which handles all chat list management
 * Managed by global modal stack
 *
 * Entity context can come from two sources:
 * 1. EntityContextProvider (when viewing a detail page)
 * 2. Modal data passed from the askMeridian action (when clicking from table row)
 */
export function AskMeridianModalContainer() {
  const entityFromProvider = useEntityContextOptional();
  const searchParams = useSearchParams();
  const { getModalData, isModalOpen, closeModal } = useGlobalModalStack();
  const isOpen = isModalOpen(MODAL_IDS.ai.askMeridianEntity);
  const data = getModalData(MODAL_IDS.ai.askMeridianEntity) as AskMeridianModalData | undefined;
  const onClose = () => closeModal(MODAL_IDS.ai.askMeridianEntity);

  // Read chatId from URL query string to restore chat when modal opens
  const chatIdFromUrl = searchParams?.get("chatId") || undefined;

  const entityFromModalData = useMemo(() => {
    if (data && typeof data === "object") {
      const { entityType, entityId, entityData, entityDisplayName } = data;

      if (entityType && entityId && entityData) {
        return {
          entityType,
          entityId,
          entityData,
          entityDisplayName: entityDisplayName || "Unknown",
        };
      }
    }
    return null;
  }, [data]);

  // Use entity from modal data if available, otherwise use provider context
  const entity = entityFromModalData || entityFromProvider;

  // On mobile the draggable/resizable FloatingModal is a poor fit (a fixed
  // 600×700 box at hard-coded coordinates). Render the focused chat in a
  // near-full-height bottom drawer instead so the user gets a distraction-free
  // conversation. `mounted` gates the branch so we never flash the desktop
  // layout before the viewport is known (MOUNT_GATE_MUST_NOT_HIDE_DATA).
  const { matches: isMobile, mounted } = useMediaQueryWithMount("(max-width: 768px)");

  if (!entity) {
    return null;
  }

  const focusedChat = (
    <MeridianEntityContextProvider
      entityType={entity.entityType}
      entityId={entity.entityId}
      entityData={entity.entityData}
      entityDisplayName={entity.entityDisplayName}
    >
      <ChatSelectionProvider initialChatId={chatIdFromUrl}>
        <ChatDataProvider>
          <div className="h-full flex flex-col">
            {/* Chat Interface - MeridianChatPage handles all chat list management */}
            <div className="flex-1 min-h-0">
              <EntityFocusedChatPageInner />
            </div>
          </div>
        </ChatDataProvider>
      </ChatSelectionProvider>
    </MeridianEntityContextProvider>
  );

  // Mobile: focused bottom drawer (open state is read from the global modal
  // stack via the shared id, so the same `ask-meridian-entity` open call drives
  // both surfaces — no second open path).
  if (mounted && isMobile) {
    return (
      <ResponsiveModal
        globalModalId={MODAL_IDS.ai.askMeridianEntity}
        side="bottom"
        title={`Ask Meridian: ${entity.entityDisplayName}`}
        className="flex flex-col"
      >
        <div className="h-full min-h-0 flex flex-col">{focusedChat}</div>
      </ResponsiveModal>
    );
  }

  // Desktop: draggable / resizable floating modal for multitasking.
  return (
    <FloatingModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Ask Meridian: ${entity.entityDisplayName}`}
      initialPosition={{ x: 100, y: 100 }}
      initialSize={{ width: 600, height: 700 }}
    >
      {focusedChat}
    </FloatingModal>
  );
}
